import { createContext, useCallback, useContext, useMemo, useState } from "react";

/**
 * Bump whenever the "What's new" content changes so the help menu highlights
 * the update as unread again.
 */
const WELCOME_GUIDE_VERSION = "v3-checklist-library-2026-10";
export const WELCOME_GUIDE_STORAGE_KEY = `welcome-guide-seen-${WELCOME_GUIDE_VERSION}`;

function hasSeenWelcomeGuide(): boolean {
  return Boolean(localStorage.getItem(WELCOME_GUIDE_STORAGE_KEY));
}

interface WelcomeGuideContextValue {
  open: boolean;
  /** True until the user has opened the current "What's new" update. */
  isUnread: boolean;
  dismiss: () => void;
  reopen: () => void;
}

const WelcomeGuideContext = createContext<WelcomeGuideContextValue | null>(null);

/**
 * "What's new" (platform updates) dialog state. The dialog never opens on its
 * own; unread updates are highlighted in the top-bar help menu instead.
 */
export function WelcomeGuideProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [isUnread, setIsUnread] = useState(() => !hasSeenWelcomeGuide());

  const dismiss = useCallback(() => setOpen(false), []);

  const reopen = useCallback(() => {
    localStorage.setItem(WELCOME_GUIDE_STORAGE_KEY, "true");
    setIsUnread(false);
    setOpen(true);
  }, []);

  const value = useMemo(
    () => ({ open, isUnread, dismiss, reopen }),
    [open, isUnread, dismiss, reopen],
  );

  return <WelcomeGuideContext.Provider value={value}>{children}</WelcomeGuideContext.Provider>;
}

export function useWelcomeGuide(): WelcomeGuideContextValue {
  const ctx = useContext(WelcomeGuideContext);
  if (!ctx) {
    throw new Error("useWelcomeGuide must be used within WelcomeGuideProvider");
  }
  return ctx;
}
